import React, { useState } from 'react';
import { Button } from './ui/button';
import { AlertTriangle, Heart, Baby, Car, Zap, Brain, X } from 'lucide-react';

const LS_KEY = 'neurowave_safety_ack_v1';

export const hasAcknowledgedSafety = () => {
  try {
    return localStorage.getItem(LS_KEY) === 'true';
  } catch {
    return false;
  }
};

export const clearSafetyAck = () => {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
};

const WARNINGS = [
  {
    icon: Zap,
    color: 'text-amber-400',
    title: 'Epilepsy & photosensitive seizures',
    text: 'Do not use if you have epilepsy, a history of seizures, or are photosensitive. Audio entrainment may trigger episodes in rare cases.',
  },
  {
    icon: Heart,
    color: 'text-rose-400',
    title: 'Pacemakers and heart conditions',
    text: 'If you wear a pacemaker or have an active cardiac condition, consult your cardiologist before using low-frequency audio sessions.',
  },
  {
    icon: Baby,
    color: 'text-pink-400',
    title: 'Pregnancy and children',
    text: 'Effects during pregnancy are not studied. Not recommended for anyone under 13 without a parent present. Use lower volumes with older children.',
  },
  {
    icon: Car,
    color: 'text-cyan-400',
    title: 'Do not operate vehicles or machinery',
    text: 'Sessions can produce drowsiness, dissociation, or altered awareness. Never use while driving, flying, operating machinery, or in any situation requiring full alertness.',
  },
  {
    icon: Brain,
    color: 'text-purple-400',
    title: 'Stop if you feel unwell',
    text: 'Discontinue immediately if you experience headache, dizziness, nausea, anxiety, or unusual sensations. Consult a clinician if symptoms persist.',
  },
];

const SafetyDisclaimer = ({ onAcknowledge, onClose, allowDismiss = false }) => {
  const [agreed, setAgreed] = useState(false);
  const checkboxRef = React.useRef(null);
  const [nudge, setNudge] = React.useState(false);

  const accept = () => {
    if (!agreed) {
      // Guide the user to the thing they missed instead of silently no-op
      setNudge(true);
      checkboxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setNudge(false), 2200);
      return;
    }
    try { localStorage.setItem(LS_KEY, 'true'); } catch { /* ignore */ }
    if (onAcknowledge) onAcknowledge();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-stretch sm:items-center justify-center sm:p-4 bg-black/80 backdrop-blur-md"
      data-testid="safety-disclaimer"
    >
      <div
        className="relative w-full sm:max-w-xl bg-gradient-to-br from-[#1a1530] to-[#0f0a1f] sm:border border-white/10 sm:rounded-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '100dvh', height: '100dvh' }}
      >
        {allowDismiss && onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/40 hover:text-white/80 z-10 p-2 -m-2"
            data-testid="safety-close-btn"
            aria-label="Close safety notes"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 pb-4">
          <div className="flex items-start gap-3 mb-5">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-white text-lg font-semibold mb-1" data-testid="safety-title">
                Before you begin — please read
              </h2>
              <p className="text-white/50 text-xs leading-relaxed">
                NeuroWave is a wellness / meditation tool. It is <strong className="text-white/70">not a medical device</strong>{' '}
                and does not diagnose, treat, cure, or prevent any disease.
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-5">
            {WARNINGS.map((w) => {
              const Icon = w.icon;
              return (
                <div
                  key={w.title}
                  className="flex gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5"
                  data-testid={`safety-warning-${w.title.split(' ')[0].toLowerCase()}`}
                >
                  <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${w.color}`} />
                  <div>
                    <p className="text-white/90 text-sm font-medium">{w.title}</p>
                    <p className="text-white/50 text-xs leading-relaxed mt-0.5">{w.text}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-3 rounded-xl bg-purple-500/8 border border-purple-500/20">
            <p className="text-white/70 text-xs leading-relaxed">
              🎧 Use headphones at safe volumes. Start at <strong className="text-white">30%</strong> and work up.
              Sessions work best in a quiet, safe space where you won't be interrupted.
            </p>
          </div>
        </div>

        {/* Sticky action bar — always visible even on a small Android screen */}
        <div className="shrink-0 border-t border-white/10 bg-[#0f0a1f]/95 backdrop-blur-sm p-4 sm:p-5" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          {!allowDismiss && (
            <label
              ref={checkboxRef}
              className={`flex items-start gap-3 mb-3 cursor-pointer select-none rounded-lg p-2 -m-2 transition-all ${nudge ? 'bg-amber-500/15 ring-2 ring-amber-400/60 animate-pulse' : ''}`}
            >
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-5 h-5 accent-purple-500 shrink-0"
                data-testid="safety-checkbox"
              />
              <span className="text-white/80 text-xs leading-relaxed">
                I have read the safety information and understand NeuroWave is for wellness and educational use only — not medical care. I will not use it while driving or operating machinery.
              </span>
            </label>
          )}

          {allowDismiss && onClose ? (
            <Button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white py-5 rounded-xl text-sm font-semibold"
              data-testid="safety-ok-btn"
            >
              Got it
            </Button>
          ) : (
            <Button
              onClick={accept}
              className={`w-full text-white py-5 rounded-xl text-sm font-semibold transition-all ${
                agreed
                  ? 'bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700'
                  : 'bg-white/10 hover:bg-white/15'
              }`}
              data-testid="safety-accept-btn"
            >
              {agreed ? 'I understand — Continue' : 'Check the box above to continue'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SafetyDisclaimer;
